// /memories — Memory Shards surface. The fragments of the Ember's
// forgotten past that the Bond has recovered. Strictly lore-aligned:
// shows only `letter_type='memory_shard'` letters, never the internal
// `memory` table (personal facts, boundaries, recurring themes —
// those are LLM plumbing and stay invisible to keep the spirit-not-
// tracker framing intact).

import type { BondDto, LetterDto } from '@athrelaris/shared';
import { Link, createFileRoute, redirect } from '@tanstack/react-router';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { switchLocaleToBond } from '../i18n/index.js';
import { getMemoryShards, getMyBond } from '../lib/api';
import { type PollScheduleTier, useAdaptivePoll } from '../lib/use-adaptive-poll';
import { authClient, getSessionSafe } from '../lib/auth-client';
import { letterExcerpt, relativeTime } from '../lib/letter-format';
import { useEmberTheme } from '../lib/use-ember-theme';
import { redirectIfLandingOnly } from '../lib/landing-mode';
// Memories is a top-level surface (reachable from the global header
// next to Letters / Leave), not a child of /inbox — so the page
// intentionally does not render an "← Inbox" back link. Header is the
// only nav between the three top-level surfaces.

const MEMORIES_SCHEDULE: PollScheduleTier[] = [
  { thresholdMs: 0, intervalMs: 30_000 },
  { thresholdMs: 5 * 60_000, intervalMs: 2 * 60_000 },
];

export const Route = createFileRoute('/memories')({
  beforeLoad: async () => {
    redirectIfLandingOnly();
    const data = await getSessionSafe();
    if (!data?.session) throw redirect({ to: '/login' });
  },
  loader: async () => {
    const bond = await getMyBond();
    if (!bond) throw redirect({ to: '/awakening' });
    const shards = await getMemoryShards();
    return { bond, shards };
  },
  component: MemoriesPage,
});

function MemoriesPage() {
  const { t } = useTranslation();
  const initial = Route.useLoaderData();
  const [bond] = useState<BondDto>(initial.bond);
  const [shards, setShards] = useState<LetterDto[]>(initial.shards);

  useEmberTheme(bond.emberColorPalette);
  useEffect(() => {
    switchLocaleToBond(bond.locale);
  }, [bond.locale]);

  // New shards arrive infrequently — driven by IG events or by the
  // memory pipeline crossing a threshold. A slow refresh is enough.
  const refresh = useCallback(async () => {
    const fresh = await getMemoryShards();
    setShards(fresh);
  }, []);
  useAdaptivePoll(refresh, { enabled: true, schedule: MEMORIES_SCHEDULE });

  const isEmpty = shards.length === 0;

  return (
    <main className="mx-auto flex max-w-2xl flex-col pt-12 sm:pt-8 lg:pt-4">
      <header className="mb-8 text-center animate-fadeUp lg:mb-10">
        <p className="font-display text-[0.62rem] text-accent-strong uppercase tracking-[0.4em] sm:text-xs lg:text-[0.78rem]">
          {t('memories.eyebrow')}
        </p>
        <h1
          className="mt-4 font-display text-3xl text-ash-50 tracking-wide sm:text-4xl lg:text-5xl"
          style={{ fontWeight: 500 }}
        >
          {t('memories.heading')}
        </h1>
        <p className="mt-4 font-body text-ash-300 text-base italic leading-snug sm:text-lg lg:mt-6 lg:text-xl">
          {t('memories.subheading', { name: bond.emberName })}
        </p>
        <div className="mx-auto mt-7 flex max-w-xs items-center gap-3 lg:mt-9">
          <span className="hairline h-px flex-1" />
          <span aria-hidden="true" className="font-display text-accent text-sm">◇</span>
          <span className="hairline h-px flex-1" />
        </div>
      </header>

      {isEmpty ? (
        <EmptyState emberName={bond.emberName} />
      ) : (
        <ul className="space-y-7 lg:space-y-9">
          {shards.map((shard, idx) => (
            <li
              key={shard.id}
              className="animate-fadeUp"
              style={{ animationDelay: `${0.15 + idx * 0.08}s` }}
            >
              <ShardCard shard={shard} />
            </li>
          ))}
        </ul>
      )}

      <footer className="mt-16 border-paper-800/50 border-t pt-8 text-center font-body text-ash-500 text-xs italic lg:mt-20 lg:text-sm">
        <p>{t('memories.footer', { name: bond.emberName })}</p>
      </footer>
    </main>
  );
}

function ShardCard({ shard }: { shard: LetterDto }) {
  const { t } = useTranslation();
  const isUnread = !shard.readAt;
  const excerpt = letterExcerpt(shard.body);

  return (
    <Link
      to="/letter/$letterId"
      params={{ letterId: shard.id }}
      className={`group relative block rounded-sm border-l-2 py-5 pr-5 pl-7 transition sm:py-6 sm:pl-9 lg:py-7 lg:pl-10 ${
        isUnread
          ? 'border-accent bg-paper-900/40 hover:bg-paper-800/50'
          : 'border-ash-700/60 bg-paper-900/20 hover:bg-paper-900/30'
      }`}
    >
      <span
        aria-hidden="true"
        className={`-translate-y-1/2 absolute top-1/2 left-2 text-lg sm:left-3 sm:text-xl lg:text-2xl ${
          isUnread ? 'animate-pulseCore text-accent-strong' : 'text-ash-500'
        }`}
      >
        ◇
      </span>

      <p
        className={`font-body text-[1.04rem] italic leading-relaxed sm:text-[1.1rem] lg:text-[1.16rem] ${
          isUnread ? 'text-ash-100' : 'text-ash-300'
        }`}
      >
        “{excerpt}”
      </p>

      <p className="mt-4 flex items-center gap-3 font-display text-[0.6rem] text-ash-500 uppercase tracking-[0.34em] sm:text-[0.66rem]">
        <span>{t('memories.recovered', { when: relativeTime(shard.generatedAt) })}</span>
        {isUnread && (
          <>
            <span aria-hidden="true" className="text-ash-700">·</span>
            <span className="text-accent-strong">{t('memories.breakSeal')}</span>
          </>
        )}
      </p>
    </Link>
  );
}

function EmptyState({ emberName }: { emberName: string }) {
  const { t } = useTranslation();
  return (
    <div className="mx-auto max-w-md text-center font-body text-ash-300 italic leading-relaxed lg:text-[1.05rem]">
      <SwarmShards />
      <p className="mt-6 lg:mt-8">{t('memories.empty', { name: emberName })}</p>
    </div>
  );
}

// Empty-state visual for /memories: a swarm of ~80 small particles
// orbiting a vacant center. Implemented as a canvas with a tiny
// physics loop — each particle has its own velocity, brownian jitter,
// soft attraction toward the center when it drifts too far, and a
// breathing alpha. ~15% of the particles are tinted with the Ember
// accent so the swarm carries a hint of the Bond's color without
// fighting the dark-storybook neutrals.
//
// CSS-only versions read as mechanical the moment you watch them for
// more than a few seconds; the random walk + soft cohesion here is
// what makes the cluster feel alive instead of choreographed.
function SwarmShards() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const SIZE_CSS = 320;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = SIZE_CSS * dpr;
    canvas.height = SIZE_CSS * dpr;
    canvas.style.width = `${SIZE_CSS}px`;
    canvas.style.height = `${SIZE_CSS}px`;
    ctx.scale(dpr, dpr);

    // Read accent + body text triplets from the live CSS variables so
    // the swarm re-tints when the Ember theme is applied. Fallbacks
    // match the locked Athrelaris brand.
    const cs = getComputedStyle(document.documentElement);
    const readVar = (name: string, fallback: string): string => {
      const v = cs.getPropertyValue(name).trim();
      return v || fallback;
    };
    const accentRgb = readVar('--color-accent', '201 102 63');
    const ashRgb = readVar('--color-text-secondary', '160 141 114');

    interface Particle {
      x: number;
      y: number;
      vx: number;
      vy: number;
      r: number;
      baseAlpha: number;
      alphaPhase: number;
      isAccent: boolean;
    }

    const COUNT = 140;
    const center = { x: SIZE_CSS / 2, y: SIZE_CSS / 2 };
    // Bigger radius + weaker pull = particles travel real distance
    // before being nudged back. Lower jitter so the motion reads as
    // movement, not vibration. Higher max speed so they cover ground.
    const COHESION_RADIUS = 140;
    const MAX_SPEED = 3.4;
    const JITTER = 0.10;
    const COHESION_STRENGTH = 0.025;
    const DAMPING = 0.965;

    const particles: Particle[] = Array.from({ length: COUNT }, () => {
      const angle = Math.random() * Math.PI * 2;
      const radius = 20 + Math.random() * 80;
      return {
        x: center.x + Math.cos(angle) * radius,
        y: center.y + Math.sin(angle) * radius,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.3,
        r: 0.6 + Math.random() * 1.6,
        baseAlpha: 0.18 + Math.random() * 0.4,
        alphaPhase: Math.random() * Math.PI * 2,
        isAccent: Math.random() < 0.15,
      };
    });

    let raf = 0;
    let frame = 0;
    let running = true;

    const tick = () => {
      if (!running) return;
      frame++;
      ctx.clearRect(0, 0, SIZE_CSS, SIZE_CSS);

      for (const p of particles) {
        // Brownian jitter — small random nudge each frame.
        p.vx += (Math.random() - 0.5) * JITTER;
        p.vy += (Math.random() - 0.5) * JITTER;

        // Soft cohesion: only pull a particle back toward the center
        // when it drifts past COHESION_RADIUS. Inside the radius the
        // particle moves freely, which is what gives the swarm its
        // organic shimmer.
        const dx = center.x - p.x;
        const dy = center.y - p.y;
        const dist = Math.hypot(dx, dy);
        if (dist > COHESION_RADIUS) {
          const overshoot = (dist - COHESION_RADIUS) / COHESION_RADIUS;
          p.vx += (dx / dist) * COHESION_STRENGTH * (1 + overshoot);
          p.vy += (dy / dist) * COHESION_STRENGTH * (1 + overshoot);
        }

        p.vx *= DAMPING;
        p.vy *= DAMPING;

        const speed = Math.hypot(p.vx, p.vy);
        if (speed > MAX_SPEED) {
          p.vx = (p.vx / speed) * MAX_SPEED;
          p.vy = (p.vy / speed) * MAX_SPEED;
        }

        p.x += p.vx;
        p.y += p.vy;

        // Breathing alpha — sin wave per particle phase.
        const breath = 0.5 + 0.5 * Math.sin(frame * 0.018 + p.alphaPhase);
        const alpha = p.baseAlpha * (0.55 + 0.45 * breath);
        const tone = p.isAccent ? accentRgb : ashRgb;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${tone.replaceAll(' ', ',')}, ${alpha.toFixed(3)})`;
        ctx.fill();
      }

      raf = requestAnimationFrame(tick);
    };

    // Pause the loop when the tab is hidden so we don't burn cycles
    // animating something nobody can see.
    const onVisibility = () => {
      if (document.hidden) {
        running = false;
        if (raf) cancelAnimationFrame(raf);
      } else if (!running) {
        running = true;
        tick();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    tick();

    return () => {
      running = false;
      if (raf) cancelAnimationFrame(raf);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="mx-auto block select-none"
    />
  );
}
